import { useEffect, useState } from 'react';

import { Box, Typography } from '@material-ui/core';
import dayjs from 'dayjs';

import { SettingsCard, SettingsNumberStepper } from '@vyce/core/src/components/SettingsCard';
import { AppSwitch } from '@vyce/core/src/components/inputs';
import { useDebounceValue } from '@vyce/core/src/hooks';

import { useStyles } from '../styles';

const MAX_WEEKS = 26;

interface Props {
  enabled: boolean;
  weeks: number;
  expanded: boolean;
  onExpandedChange: (expanded: boolean) => void;
  onToggle: (enabled: boolean) => void;
  onWeeksChange: (weeks: number) => void;
}

export const DormantTeamMembers = ({
  enabled,
  weeks,
  expanded,
  onExpandedChange,
  onToggle,
  onWeeksChange,
}: Props) => {
  const classes = useStyles();
  // The stepper moves one week per click, so hold the value locally and save once the
  // user stops clicking rather than firing a request per press.
  const [draftWeeks, setDraftWeeks] = useState<number>(weeks);
  const debouncedWeeks = useDebounceValue(draftWeeks, 600);

  useEffect(() => {
    setDraftWeeks(weeks);
  }, [weeks]);

  useEffect(() => {
    if (debouncedWeeks !== null && debouncedWeeks !== weeks) {
      onWeeksChange(debouncedWeeks);
    }
  }, [debouncedWeeks]);

  const cutOffDate = dayjs()
    .subtract(draftWeeks * 7, 'day')
    .format('D MMM');

  return (
    <SettingsCard
      title="Tidy up inactive people"
      description="Takes people off your locations when they haven’t clocked in anywhere for a while, so your location lists stay accurate. They keep their records and can be added back."
      status={enabled ? `On · after ${draftWeeks} ${draftWeeks === 1 ? 'week' : 'weeks'}` : 'Off'}
      statusTone={enabled ? 'on' : 'off'}
      expanded={expanded}
      onExpandedChange={onExpandedChange}
      headerControl={
        <AppSwitch
          checked={enabled}
          onChange={(_, checked) => onToggle(checked)}
          inputProps={{ 'aria-label': 'Tidy up inactive people' }}
        />
      }
    >
      <Box display="flex" alignItems="center" gridGap={12} flexWrap="wrap">
        <Typography variant="caption">Remove after</Typography>

        <SettingsNumberStepper
          value={draftWeeks}
          max={MAX_WEEKS}
          ariaLabel="weeks with no clock in"
          onChange={setDraftWeeks}
        />

        <Typography variant="caption">weeks with no clock in</Typography>
      </Box>

      <Typography variant="caption" className={classes.exampleText}>
        With {draftWeeks} {draftWeeks === 1 ? 'week' : 'weeks'}, anyone whose last clock in was before{' '}
        {cutOffDate} would be removed from their locations tonight.
      </Typography>
    </SettingsCard>
  );
};
